/**
 * 進出時讀寫本機存檔；引擎就緒後還原高度圖。
 */

import { useEffect, useRef } from 'react'
import { useGameStore } from '../core/store/gameStore'
import type { ErosionEngine } from '../core/ErosionEngine'
import {
  applyTerrain,
  readWorldSave,
  snapshotTerrain,
  writeWorldSave,
  type WorldSave,
} from './worldSave'

export function hydrateWorldSave(save: WorldSave) {
  applyTerrain(save)
  const st = useGameStore.getState()
  st.setSpeciesList(save.species ?? [])
  st.setPlacements(save.placements ?? [])
  st.setSightings(save.sightings ?? [])
  if (save.placedObjects) {
    for (const o of [...st.placedObjects]) st.removeObject(o.id)
    for (const o of save.placedObjects) st.addObject(o)
  }
  if (save.mapLabels) {
    for (const l of [...st.mapLabels]) st.removeLabel(l.id)
    for (const l of save.mapLabels) st.addLabel(l)
  }
  if (save.worldId) useGameStore.setState({ worldId: save.worldId })
}

function collectSave(worldId: string): WorldSave {
  const s = useGameStore.getState()
  const terrain = snapshotTerrain()
  return {
    worldId,
    ...terrain,
    placedObjects: s.placedObjects,
    mapLabels: s.mapLabels,
    species: s.species,
    placements: s.placements,
    sightings: s.sightings,
    savedAt: new Date().toISOString(),
  }
}

export function useWorldPersist(engine: ErosionEngine | null, heightmapReady: boolean) {
  const loaded = useRef(false)
  const worldId = useGameStore(s => s.worldId)

  useEffect(() => {
    if (!heightmapReady || !engine || loaded.current) return
    loaded.current = true
    void readWorldSave().then((save) => {
      if (!save) return
      hydrateWorldSave(save)
      if (engine) engine.restoreHeightmap(new Float32Array(save.heightmap))
    }).catch((err) => console.warn('[我的山海] 讀檔失敗', err))
  }, [engine, heightmapReady])

  useEffect(() => {
    const persist = () => {
      if (!heightmapReady) return
      const id = useGameStore.getState().worldId
      void writeWorldSave(collectSave(id)).catch((err) => console.warn('[我的山海] 存檔失敗', err))
    }
    const t = window.setInterval(persist, 8000)
    window.addEventListener('beforeunload', persist)
    return () => {
      window.clearInterval(t)
      window.removeEventListener('beforeunload', persist)
      persist()
    }
  }, [heightmapReady, worldId])
}

export { collectSave }
